import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Eye, ExternalLink, Play } from "lucide-react";
import { VIDEOS } from "../data/content";

function renderContent(text) {
  return text.split("\n\n").map((block, i) => {
    if (block.startsWith("## ")) {
      return (
        <h2 key={i} className="font-sans font-bold text-xl text-gray-800 mt-8 mb-3">
          {block.replace("## ", "")}
        </h2>
      );
    }
    if (block.startsWith("> ")) {
      return (
        <blockquote key={i} className="border-l-4 border-pink-300 bg-pink-50/60 rounded-r-2xl px-5 py-3 my-4 font-sans text-gray-600 text-sm italic">
          {block.replace("> ", "")}
        </blockquote>
      );
    }
    if (block.startsWith("[youtube:")) {
      const videoId = block.slice(9, -1);
      return (
        <div key={i} className="w-full rounded-2xl overflow-hidden my-6 relative" style={{ paddingBottom: "56.25%" }}>
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            title="YouTube video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        </div>
      );
    }
    const parts = block.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).map((part, j) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={j}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith("*") && part.endsWith("*")) {
        return <em key={j}>{part.slice(1, -1)}</em>;
      }
      return part;
    });
    return (
      <p key={i} className="font-sans text-gray-700 text-base leading-relaxed mb-4">
        {parts}
      </p>
    );
  });
}

export default function VideoPost() {
  const { id } = useParams();
  const video = VIDEOS.find((v) => v.id === Number(id));

  if (!video) return <Navigate to="/videos" replace />;

  const currentIndex = VIDEOS.findIndex((v) => v.id === video.id);
  const prev = VIDEOS[currentIndex - 1] || null;
  const next = VIDEOS[currentIndex + 1] || null;

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">

      <Link to="/videos" className="inline-flex items-center gap-2 font-mono text-sm text-gray-500 hover:text-gray-800 transition-colors mb-8">
        <ArrowLeft size={16} /> Quay lại Videos
      </Link>

      {/* Thumbnail / Embed */}
      {video.embedUrl ? (
        <div className="w-full rounded-3xl overflow-hidden mb-8 relative" style={{ paddingBottom: "56.25%" }}>
          <iframe
            src={video.embedUrl}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        </div>
      ) : (
        <div className={`relative w-full h-56 rounded-3xl bg-gradient-to-br ${video.bg} flex items-center justify-center mb-8 overflow-hidden`}>
          <span className="text-7xl">{video.emoji}</span>
          <div className="absolute inset-0 flex items-center justify-center bg-black/10">
            <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
              <Play size={26} className="text-gray-700 ml-1" fill="currentColor" />
            </div>
          </div>
          <span className="absolute bottom-4 right-4 font-mono text-sm bg-black/60 text-white px-3 py-1 rounded-xl">
            {video.duration}
          </span>
        </div>
      )}

      <h1 className="font-sans font-bold text-2xl md:text-3xl text-gray-900 leading-snug mb-4">
        {video.title}
      </h1>

      <div className="mb-8 pb-8 border-b border-gray-400"></div>

      <div>
        {renderContent(video.content)}
      </div>

      <div className="mt-12 grid grid-cols-2 gap-4">
        {prev ? (
          <Link to={`/videos/${prev.id}`} className="card hover:-translate-y-1 transition-all duration-300 p-5">
            <p className="font-sans text-xs text-gray-400 mb-2">← Video trước</p>
            <p className="font-sans font-semibold text-sm text-gray-700 line-clamp-2 leading-relaxed">{prev.title}</p>
          </Link>
        ) : <div />}
        {next ? (
          <Link to={`/videos/${next.id}`} className="card hover:-translate-y-1 transition-all duration-300 text-right p-5">
            <p className="font-sans text-xs text-gray-400 mb-2">Video sau →</p>
            <p className="font-sans font-semibold text-sm text-gray-700 line-clamp-2 leading-relaxed">{next.title}</p>
          </Link>
        ) : <div />}
      </div>

    </div>
  );
}
